/**
 * The queue, for anything that renders it, and the one piece of formatting
 * worth naming.
 *
 * Split out of PendingQueuePanel so the panel exports a component and nothing
 * else: three surfaces read the queue — the header badge, the kiosk line and
 * the panel itself — and a hook living inside one of them would make the other
 * two import a component to get at it.
 */

import { useEffect, useState } from 'react';
import { subscribe, type QueueState } from '@/services/pendingQueue';

/** The live queue. Emits immediately on subscribe, so a first render is never
 *  wrong about the count. */
export function usePendingQueue(): QueueState {
  const [state, setState] = useState<QueueState>({ waiting: [], blocked: [] });
  useEffect(() => subscribe(setState), []);
  return state;
}

/**
 * How long something has been sitting there.
 *
 * Whole minutes, then hours, then days. Deliberately coarse: the useful fact
 * is "since this morning", not the second — and a clock that has gone
 * backwards (a device correcting its time, which happens on kiosks left
 * unplugged) reads as "just now" rather than a negative age.
 */
export function ageOf(from: number, now: number = Date.now()): string {
  const seconds = Math.max(0, Math.round((now - from) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.round(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}
